import { User, Meeting, Participant, ChatMessage, SharedFile } from '../types';

export const API_BASE =
  import.meta.env.VITE_API_URL ||
  (window.location.hostname === 'localhost'
    ? 'https://cloudflare-meet-api.icab.workers.dev'
    : 'https://cloudflare-meet-api.icab.workers.dev');

function getToken(): string | null {
  return localStorage.getItem('cfmeet_token');
}

export function setToken(token: string): void {
  localStorage.setItem('cfmeet_token', token);
}

export function clearToken(): void {
  localStorage.removeItem('cfmeet_token');
}

async function fetchApi<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers || {});
  if (!headers.has('Content-Type') && !(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }

  const token = getToken();
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
    credentials: 'include',
  });

  const contentType = res.headers.get('Content-Type') || '';
  let data: any = null;
  if (contentType.includes('application/json')) {
    data = await res.json();
  } else {
    data = await res.text();
  }

  if (!res.ok) {
    const errorMsg = data?.error?.message || data?.error?.code || `Request failed (${res.status})`;
    const err = new Error(errorMsg);
    (err as any).code = data?.error?.code;
    (err as any).status = res.status;
    throw err;
  }

  return data as T;
}

export const api = {
  // Auth
  async register(email: string, password: string, name: string): Promise<{ user: User; token: string }> {
    const data = await fetchApi<{ success: boolean; user: User; token: string }>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, name }),
    });
    if (data.token) setToken(data.token);
    return { user: data.user, token: data.token };
  },

  async login(email: string, password: string): Promise<{ user: User; token: string }> {
    const data = await fetchApi<{ success: boolean; user: User; token: string }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    if (data.token) setToken(data.token);
    return { user: data.user, token: data.token };
  },

  async logout(): Promise<void> {
    try {
      await fetchApi('/api/auth/logout', { method: 'POST' });
    } finally {
      clearToken();
    }
  },

  async getMe(): Promise<User> {
    return fetchApi<User>('/api/auth/me');
  },

  async updateProfile(name: string): Promise<User> {
    const res = await fetchApi<{ success: boolean; user: User }>('/api/profile', {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    });
    return res.user;
  },

  async uploadAvatar(file: File): Promise<{ avatarUrl: string }> {
    const form = new FormData();
    form.append('avatar', file);
    return fetchApi<{ avatarUrl: string }>('/api/auth/avatar', {
      method: 'POST',
      body: form,
    });
  },

  // Meetings
  async createMeeting(title?: string, password?: string): Promise<{ publicId: string; title: string; hasPassword: boolean }> {
    return fetchApi('/api/meetings', {
      method: 'POST',
      body: JSON.stringify({ title, password }),
    });
  },

  async getMeetings(): Promise<Meeting[]> {
    return fetchApi<Meeting[]>('/api/meetings');
  },

  async getMeeting(publicId: string): Promise<Meeting> {
    return fetchApi<Meeting>(`/api/meetings/${encodeURIComponent(publicId)}`);
  },

  async joinMeeting(
    publicId: string,
    password?: string,
    name?: string
  ): Promise<{ success: boolean; publicId: string; isHost: boolean; token?: string; user?: User }> {
    const res = await fetchApi<{
      success: boolean;
      publicId: string;
      isHost: boolean;
      token?: string;
      user?: User;
    }>(`/api/meetings/${encodeURIComponent(publicId)}/join`, {
      method: 'POST',
      body: JSON.stringify({ password, name }),
    });
    if (res.token) {
      setToken(res.token);
    }
    return res;
  },

  async leaveMeeting(publicId: string): Promise<void> {
    await fetchApi(`/api/meetings/${encodeURIComponent(publicId)}/leave`, { method: 'POST' });
  },

  async endMeeting(publicId: string): Promise<void> {
    await fetchApi(`/api/meetings/${encodeURIComponent(publicId)}/end`, { method: 'POST' });
  },

  async getParticipants(publicId: string): Promise<Participant[]> {
    return fetchApi<Participant[]>(`/api/meetings/${encodeURIComponent(publicId)}/participants`);
  },

  async removeParticipant(publicId: string, participantId: string): Promise<void> {
    await fetchApi(`/api/meetings/${encodeURIComponent(publicId)}/participants/${encodeURIComponent(participantId)}/remove`, {
      method: 'POST',
    });
  },

  async muteParticipant(publicId: string, participantId: string): Promise<void> {
    await fetchApi(`/api/meetings/${encodeURIComponent(publicId)}/participants/${encodeURIComponent(participantId)}/mute`, {
      method: 'POST',
    });
  },

  async muteAll(publicId: string): Promise<void> {
    await fetchApi(`/api/meetings/${encodeURIComponent(publicId)}/mute-all`, {
      method: 'POST',
    });
  },

  async getChatHistory(publicId: string): Promise<ChatMessage[]> {
    return fetchApi<ChatMessage[]>(`/api/meetings/${encodeURIComponent(publicId)}/chat`);
  },

  // Files
  async uploadFile(publicId: string, file: File): Promise<{ success: boolean; fileId: string; fileName: string; size: number }> {
    const form = new FormData();
    form.append('file', file);
    return fetchApi(`/api/meetings/${encodeURIComponent(publicId)}/files`, {
      method: 'POST',
      body: form,
    });
  },

  async getFiles(publicId: string): Promise<SharedFile[]> {
    return fetchApi<SharedFile[]>(`/api/meetings/${encodeURIComponent(publicId)}/files`);
  },

  getFileDownloadUrl(publicId: string, fileId: string): string {
    const token = getToken();
    const q = token ? `?token=${encodeURIComponent(token)}` : '';
    return `${API_BASE}/api/meetings/${encodeURIComponent(publicId)}/files/${encodeURIComponent(fileId)}${q}`;
  },

  // Realtime ICE
  async getIceServers(publicId: string): Promise<{ iceServers: RTCIceServer[] }> {
    return fetchApi<{ iceServers: RTCIceServer[] }>(`/api/meetings/${encodeURIComponent(publicId)}/realtime/ice`);
  },

  // Realtime SFU
  async createSfuSessions(publicId: string): Promise<{ publishSessionId: string; subscribeSessionId: string }> {
    return fetchApi(`/api/meetings/${encodeURIComponent(publicId)}/realtime/session`, {
      method: 'POST',
    });
  },

  async publishSfu(publicId: string, sessionId: string, sessionDescription: RTCSessionDescriptionInit, tracks: any[]): Promise<any> {
    return fetchApi(`/api/meetings/${encodeURIComponent(publicId)}/realtime/publish`, {
      method: 'POST',
      body: JSON.stringify({ sessionId, sessionDescription, tracks }),
    });
  },

  async subscribeSfu(publicId: string, sessionId: string, tracks: any[]): Promise<any> {
    return fetchApi(`/api/meetings/${encodeURIComponent(publicId)}/realtime/subscribe`, {
      method: 'POST',
      body: JSON.stringify({ sessionId, tracks }),
    });
  },

  async renegotiateSfu(publicId: string, sessionId: string, sessionDescription: RTCSessionDescriptionInit): Promise<any> {
    return fetchApi(`/api/meetings/${encodeURIComponent(publicId)}/realtime/renegotiate`, {
      method: 'POST',
      body: JSON.stringify({ sessionId, sessionDescription }),
    });
  },
};
